// src/components/PurchasesTab.js — Admin → People → Purchases.
// Shows ONLY program purchases that didn't reach their member on their own
// (after a one-hour grace window): not connected to any account, or the chosen
// pod couldn't be joined. "All clear" is the normal state. Each card carries
// the context to decide without leaving the screen, and one action fixes it.
// Module-scope components only; hover via useState.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { subscribeAttentionPurchases, linkPurchaseToMember, dismissPurchase, runPurchaseCheckNow } from '../services/purchases';
import { fetchPrograms, fetchPods } from '../services/firebase';

const SOFT = 'color-mix(in srgb, var(--mom-charcoal) 72%, var(--mom-cream))';
const FAINT = 'color-mix(in srgb, var(--mom-charcoal) 52%, var(--mom-cream))';
const HAIR = 'color-mix(in srgb, var(--mom-charcoal) 9%, #fff)';
const SANS = 'var(--mom-font-sans)';
const SERIF = 'var(--mom-font-serif)';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const nameOf = (u) => (u ? (`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email) : '');

function Btn({ primary, onClick, disabled, children }) {
    const [hover, setHover] = useState(false);
    return (
        <button onClick={onClick} disabled={disabled} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={primary ? {
                border: 0, borderRadius: 99, padding: '10px 22px', fontFamily: SANS, fontSize: 13.5, fontWeight: 600, color: '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
                background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', opacity: disabled ? 0.5 : 1,
                boxShadow: hover && !disabled ? '0 8px 22px color-mix(in srgb, var(--mom-primary) 38%, transparent)' : '0 6px 18px color-mix(in srgb, var(--mom-primary) 26%, transparent)',
            } : { border: 0, background: 'none', padding: '8px 0', fontFamily: SANS, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: hover ? 'var(--mom-charcoal)' : SOFT, opacity: disabled ? 0.5 : 1 }}>
            {children}
        </button>
    );
}

function MemberPicker({ getToken, programId, onPick }) {
    const [term, setTerm] = useState('');
    const [results, setResults] = useState([]);
    const [busy, setBusy] = useState(false);
    const timer = useRef(null);
    useEffect(() => {
        clearTimeout(timer.current);
        if (term.trim().length < 2) { setResults([]); return undefined; }
        timer.current = setTimeout(async () => {
            setBusy(true);
            try {
                const token = await getToken();
                const r = await fetch(`/api/users?search=${encodeURIComponent(term.trim())}`, { headers: { Authorization: `Bearer ${token}` } });
                const raw = r.ok ? await r.json() : [];
                setResults((Array.isArray(raw) ? raw : (raw.users || [])).slice(0, 8));
            } catch (e) { setResults([]); }
            setBusy(false);
        }, 300);
        return () => clearTimeout(timer.current);
    }, [term, getToken]);
    return (
        <div style={{ marginTop: 12 }}>
            <input className="mom-input" autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search the member's name or app email…" style={{ fontSize: 13 }} />
            {term.trim().length >= 2 && (
                <div style={{ marginTop: 6, border: `1px solid ${HAIR}`, borderRadius: 10, background: '#fff', maxHeight: 220, overflowY: 'auto' }}>
                    {busy && <div style={{ padding: '10px 14px', fontFamily: SANS, fontSize: 12, color: SOFT }}>Searching…</div>}
                    {!busy && results.length === 0 && <div style={{ padding: '10px 14px', fontFamily: SANS, fontSize: 12, color: SOFT }}>No member found. Check the spelling, or ask her which email she signs in with.</div>}
                    {!busy && results.map((u) => <PickRow key={u.id} u={u} programId={programId} onPick={onPick} />)}
                </div>
            )}
        </div>
    );
}
function PickRow({ u, programId, onPick }) {
    const [hover, setHover] = useState(false);
    const has = Array.isArray(u.purchasedPrograms) && u.purchasedPrograms.includes(programId);
    return (
        <div onClick={() => onPick(u)} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${HAIR}`, background: hover ? 'var(--mom-cream)' : '#fff', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <div>
                <div style={{ fontFamily: SANS, fontSize: 13, color: 'var(--mom-charcoal)', fontWeight: 600 }}>{nameOf(u)}</div>
                <div style={{ fontFamily: SANS, fontSize: 11.5, color: SOFT }}>{u.email}</div>
            </div>
            {has && <span style={{ alignSelf: 'center', fontFamily: SANS, fontSize: 11, color: FAINT }}>already has this program</span>}
        </div>
    );
}

function PurchaseCard({ p, programName, pods, getToken, onDone, say }) {
    const unlinked = p.status === 'unlinked';
    const [picking, setPicking] = useState(false);
    const [member, setMember] = useState(null);
    const [podId, setPodId] = useState(p.podId || '');
    const [busy, setBusy] = useState(false);
    const [dismissing, setDismissing] = useState(false);
    const [reason, setReason] = useState('refunded');
    const chosenPod = pods.find((x) => x.id === p.podId);

    const link = async (userId) => {
        setBusy(true);
        const r = await linkPurchaseToMember(p.sessionId, userId, podId, getToken);
        setBusy(false);
        if (r.ok) {
            const rec = r.data.purchase || {};
            say(rec.podJoined === false ? `Access given — but ${rec.podIssue || 'the pod couldn’t be joined'} Pick another pod below.` : 'Done — she has the program and her pod.');
            onDone();
        } else say(r.data.error || "Couldn't complete that. Please try again.");
    };
    const dismiss = async () => {
        setBusy(true);
        const r = await dismissPurchase(p.sessionId, reason, getToken);
        setBusy(false);
        if (r.ok) { say('Marked as handled.'); onDone(); } else say("Couldn't save that. Please try again.");
    };

    return (
        <div style={{ background: '#fff', borderRadius: 20, padding: '22px 24px', marginBottom: 16, boxShadow: '0 8px 26px rgba(62,47,47,.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                <span style={{ fontFamily: SANS, fontSize: 10.5, letterSpacing: '0.13em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--mom-primary-dark, var(--mom-primary))' }}>
                    {unlinked ? 'Paid · not connected to an account' : 'Paid · couldn’t join the pod'}
                </span>
                <div style={{ textAlign: 'right', fontFamily: SANS, fontSize: 12, color: SOFT, lineHeight: 1.5 }}>
                    <b style={{ color: 'var(--mom-charcoal)', fontWeight: 600 }}>{programName}</b><br />{fmtDate(p.purchasedAt)}{p.amount != null ? ` · $${p.amount}` : ''}
                </div>
            </div>
            <div style={{ marginTop: 12, fontFamily: SANS, fontSize: 13.5, lineHeight: 1.7, color: 'var(--mom-charcoal)' }}>
                <div><span style={{ color: SOFT }}>Paid by </span><b>{p.name || 'No name given'}</b> <span style={{ color: SOFT }}>· {p.email}</span></div>
                {chosenPod && <div><span style={{ color: SOFT }}>Chose </span>{chosenPod.podName}</div>}
                {!unlinked && <div><span style={{ color: SOFT }}>Member </span>{p.linkedEmail || p.linkedUserId}</div>}
            </div>
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${HAIR}`, fontFamily: SANS, fontSize: 13, color: SOFT, lineHeight: 1.6 }}>
                {unlinked
                    ? 'No app account uses this email, so she can’t open what she bought. She likely signs in with a different email, or mistyped this one. Find her below — she’ll get the program, her pod and the welcome email.'
                    : (p.podIssue || 'Her pod couldn’t be joined automatically.') + ' She already has the program; choose a pod to add her to.'}
            </div>

            {(unlinked ? member : true) && pods.length > 0 && (
                <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: SANS, fontSize: 12.5, color: SOFT }}>Pod</span>
                    <select value={podId} onChange={(e) => setPodId(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${HAIR}`, fontFamily: SANS, fontSize: 13 }}>
                        {pods.map((x) => <option key={x.id} value={x.id}>{x.podName} ({(x.participants || []).length}/{x.maxParticipants || '∞'}){x.id === p.podId ? ' — her choice' : ''}</option>)}
                    </select>
                </div>
            )}

            {unlinked && member && (
                <div style={{ marginTop: 14, padding: '12px 14px', background: 'var(--mom-cream)', borderRadius: 12, fontFamily: SANS, fontSize: 13, color: 'var(--mom-charcoal)' }}>
                    Give <b>{nameOf(member)}</b> ({member.email}) <b>{programName}</b>{podId ? <> and add her to <b>{(pods.find((x) => x.id === podId) || {}).podName}</b></> : ''}?
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 16, flexWrap: 'wrap' }}>
                {unlinked && !member && !picking && <Btn primary onClick={() => setPicking(true)}>Link to member…</Btn>}
                {unlinked && member && <Btn primary disabled={busy} onClick={() => link(member.id)}>{busy ? 'Linking…' : 'Link and give access'}</Btn>}
                {unlinked && member && <Btn onClick={() => { setMember(null); setPicking(true); }}>Choose someone else</Btn>}
                {!unlinked && <Btn primary disabled={busy || !podId} onClick={() => link(p.linkedUserId)}>{busy ? 'Adding…' : 'Add to this pod'}</Btn>}
                {!dismissing && <Btn onClick={() => setDismissing(true)}>Mark as handled</Btn>}
                {dismissing && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <select value={reason} onChange={(e) => setReason(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${HAIR}`, fontFamily: SANS, fontSize: 13 }}>
                            <option value="refunded">Refunded</option>
                            <option value="handled">Sorted out another way</option>
                            <option value="duplicate">Duplicate purchase</option>
                            <option value="other">Other</option>
                        </select>
                        <Btn primary disabled={busy} onClick={dismiss}>Confirm</Btn>
                        <Btn onClick={() => setDismissing(false)}>Cancel</Btn>
                    </span>
                )}
            </div>
            {unlinked && picking && !member && <MemberPicker getToken={getToken} programId={p.programId} onPick={(u) => { setMember(u); setPicking(false); }} />}
        </div>
    );
}

export default function PurchasesTab({ getToken }) {
    const [items, setItems] = useState(null);
    const [programs, setPrograms] = useState([]);
    const [podsByProgram, setPodsByProgram] = useState({});
    const [toast, setToast] = useState(null);
    const [checking, setChecking] = useState(false);

    useEffect(() => subscribeAttentionPurchases(setItems), []);
    useEffect(() => { fetchPrograms().then((p) => setPrograms(p || [])).catch(() => {}); }, []);
    const programIds = (items || []).map((p) => p.programId).filter(Boolean).sort().join(',');
    useEffect(() => {
        const ids = programIds ? [...new Set(programIds.split(','))] : [];
        ids.forEach((id) => { fetchPods(id).then((pods) => setPodsByProgram((prev) => ({ ...prev, [id]: pods || [] }))).catch(() => {}); });
    }, [programIds]);

    const say = useCallback((message) => { setToast(message); setTimeout(() => setToast((cur) => (cur === message ? null : cur)), 4000); }, []);
    const refreshPods = (programId) => fetchPods(programId).then((pods) => setPodsByProgram((prev) => ({ ...prev, [programId]: pods || [] }))).catch(() => {});
    const checkNow = async () => {
        setChecking(true);
        const r = await runPurchaseCheckNow(getToken);
        setChecking(false);
        say(r.ok ? (r.data.linked ? `Linked ${r.data.linked} purchase${r.data.linked === 1 ? '' : 's'} automatically.` : 'Checked — nothing new to link.') : "Couldn't run the check. Please try again.");
    };

    const n = (items || []).length;
    return (
        <div style={{ maxWidth: 820 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
                <h2 style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 600, fontSize: 30, margin: 0, color: 'var(--mom-charcoal)' }}>Purchases</h2>
                <Btn onClick={checkNow} disabled={checking}>{checking ? 'Checking…' : 'Check now'}</Btn>
            </div>
            <div style={{ width: 44, height: 2, background: 'var(--mom-primary)', opacity: 0.45, margin: '10px 0 14px', borderRadius: 1 }} />
            <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 17, color: SOFT }}>
                {items === null ? ' ' : n === 0 ? 'All clear — every program purchase reached its member.' : `${n} purchase${n === 1 ? ' needs' : 's need'} your attention.`}
            </div>
            <p style={{ fontFamily: SANS, fontSize: 12.5, color: FAINT, lineHeight: 1.6, marginTop: 8 }}>
                Purchases connect to members on their own — at checkout, on the welcome page, when she creates her account, or in an hourly check. Only what's still stuck an hour after paying shows up here.
            </p>
            <div style={{ marginTop: 22 }}>
                {items === null ? null : n === 0 ? (
                    <div style={{ textAlign: 'center', padding: '44px 20px', animation: 'fadeUp 0.3s ease' }}>
                        <div style={{ width: 64, height: 64, borderRadius: '50%', margin: '0 auto 14px', background: 'radial-gradient(circle at 35% 30%, #fff, var(--mom-primary-light, #f0d6d4))', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--mom-primary) 18%, #fff)' }}>
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--mom-primary-dark, #c05a7a)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        </div>
                        <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 19, margin: 0, color: 'var(--mom-charcoal)' }}>All clear.</p>
                    </div>
                ) : items.map((p) => (
                    <PurchaseCard key={p.sessionId} p={p} getToken={getToken} say={say}
                        programName={(programs.find((x) => x.id === p.programId) || {}).name || p.programName || 'Program'}
                        pods={podsByProgram[p.programId] || []}
                        onDone={() => refreshPods(p.programId)} />
                ))}
            </div>
            {toast && (
                <div role="status" style={{ position: 'fixed', left: '50%', bottom: 30, transform: 'translateX(-50%)', zIndex: 3300, background: 'var(--mom-charcoal)', color: '#fff', fontFamily: SANS, fontSize: 13, padding: '11px 18px', borderRadius: 99, animation: 'fadeUp 0.2s ease', maxWidth: '90vw' }}>{toast}</div>
            )}
        </div>
    );
}
